import { createSlice, PayloadAction, Dispatch } from "@reduxjs/toolkit";
import { getServicesManager } from "../../../services";
import { ProvisionedTeam } from "../../../interfaces/provisioned";
import { TeamResult } from "../../../interfaces/users";
import { fetchInboundRoutes, clearRoutes } from "../routes/slice";

export interface ProvisionedTeamSelectorState {
  selectedTeamId: string | null;
  availableTeams: ProvisionedTeam[];
  isLoading: boolean;
  isProvisioning: boolean;
  isGrantingAccess: boolean;
  error: string | null;
  lastMultidialerSuccess: boolean | null;
  isCreationModalOpen: boolean;
  showNewTeamCreation: boolean;
}

const provisionedTeamSelectorInitialState: ProvisionedTeamSelectorState = {
  selectedTeamId: null,
  availableTeams: [],
  isLoading: false,
  isProvisioning: false,
  isGrantingAccess: false,
  error: null,
  lastMultidialerSuccess: null,
  isCreationModalOpen: false,
  showNewTeamCreation: false,
};

export const provisionedTeamSelectorSlice = createSlice({
  name: 'provisionedTeamSelector',
  initialState: provisionedTeamSelectorInitialState,
  reducers: {
    updateSelectedTeam: (state, action: PayloadAction<string>) => {
      state.selectedTeamId = action.payload;
      state.error = null;
      // Update services manager directly with the new team ID
      getServicesManager().updateSelectedTeamId(action.payload);
    },
    updateAvailableTeams: (state, action: PayloadAction<ProvisionedTeam[]>) => {
      state.availableTeams = action.payload;
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    setProvisioning: (state, action: PayloadAction<boolean>) => {
      state.isProvisioning = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    setGrantingAccess: (state, action: PayloadAction<boolean>) => {
      state.isGrantingAccess = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isLoading = false;
      state.isProvisioning = false;
      state.isGrantingAccess = false;
    },
    clearSelection: (state) => {
      state.selectedTeamId = null;
      state.error = null;
      // Clear team ID in services manager
      getServicesManager().clearSelectedTeamId();
    },
    clearError: (state) => {
      state.error = null;
    },
    addNewTeam: (state, action: PayloadAction<ProvisionedTeam>) => {
      state.availableTeams.push(action.payload);
      state.isProvisioning = false;
      state.isGrantingAccess = false;
      state.error = null;
    },
    updateTeamName: (state, action: PayloadAction<{ teamId: string; newName: string }>) => {
      const team = state.availableTeams.find(t => t.team_id === action.payload.teamId);
      if (team) {
        team.team_name = action.payload.newName;
      }
      state.error = null;
    },
    setMultidialerSuccess: (state, action: PayloadAction<boolean>) => {
      state.lastMultidialerSuccess = action.payload;
    },
    setCreationModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isCreationModalOpen = action.payload;
    },
    setShowNewTeamCreation: (state, action: PayloadAction<boolean>) => {
      state.showNewTeamCreation = action.payload;
    },
  }
});

export const { 
  updateSelectedTeam, 
  updateAvailableTeams, 
  setLoading, 
  setProvisioning,
  setGrantingAccess,
  setError,
  clearSelection, 
  clearError,
  addNewTeam,
  updateTeamName,
  setMultidialerSuccess,
  setCreationModalOpen,
  setShowNewTeamCreation
} = provisionedTeamSelectorSlice.actions;

// Enhanced action creator that also refreshes routes
export const updateSelectedTeamWithRoutesRefresh = (teamId: string) => async (dispatch: Dispatch) => {
  dispatch(updateSelectedTeam(teamId));
  dispatch(fetchInboundRoutes() as any);
};

// Enhanced clear action that also clears routes
export const clearSelectionWithRoutesRefresh = () => async (dispatch: Dispatch) => {
  dispatch(clearSelection());
  dispatch(clearRoutes());
};